import React, { useState } from 'react';
import { 
  Share2, Copy, Check, Facebook, Instagram, Loader2, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import api from '../utils/api';

export default function SocialListingGenerator() {
  const [form, setForm] = useState({
    make: '', model: '', year: '', price: '', mileage: '', condition: 'Good', color: '', features: ''
  });
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const handleGenerate = async () => {
    if (!form.make || !form.model || !form.year) {
      toast.error('Make, model, and year are required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        make: form.make,
        model: form.model,
        year: parseInt(form.year),
        price: form.price ? parseFloat(form.price) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        condition: form.condition,
        color: form.color || null,
        features: form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : [],
        platform: 'both'
      };
      const { data } = await api.post('/api/ai/social-listing', payload);
      setListing(data);
      toast.success('Listings generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate listings');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(''), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div data-testid="social-listing-generator">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center shadow-lg">
          <Share2 className="w-7 h-7 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 text-xl">Social Media Listing Generator</h3>
          <p className="text-gray-500">Create optimized listings for Facebook Marketplace & Instagram</p>
        </div>
      </div>

      {/* Vehicle Details Form */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-gray-700 font-medium text-sm">Make *</Label>
            <Input placeholder="Toyota" value={form.make} onChange={e => setForm(p => ({ ...p, make: e.target.value }))} className="h-10 rounded-xl" data-testid="social-make" />
          </div>
          <div>
            <Label className="text-gray-700 font-medium text-sm">Model *</Label>
            <Input placeholder="Camry" value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} className="h-10 rounded-xl" data-testid="social-model" />
          </div>
          <div>
            <Label className="text-gray-700 font-medium text-sm">Year *</Label>
            <Input type="number" placeholder="2020" value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} className="h-10 rounded-xl" data-testid="social-year" />
          </div>
          <div>
            <Label className="text-gray-700 font-medium text-sm">Price</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input type="number" placeholder="25000" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="pl-7 h-10 rounded-xl" data-testid="social-price" />
            </div>
          </div>
          <div>
            <Label className="text-gray-700 font-medium text-sm">Mileage</Label>
            <Input type="number" placeholder="45000" value={form.mileage} onChange={e => setForm(p => ({ ...p, mileage: e.target.value }))} className="h-10 rounded-xl" data-testid="social-mileage" />
          </div>
          <div>
            <Label className="text-gray-700 font-medium text-sm">Color</Label>
            <Input placeholder="Silver" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="h-10 rounded-xl" data-testid="social-color" />
          </div>
          <div>
            <Label className="text-gray-700 font-medium text-sm">Condition</Label>
            <Select value={form.condition} onValueChange={v => setForm(p => ({ ...p, condition: v }))}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="social-condition-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Excellent">Excellent</SelectItem>
                <SelectItem value="Good">Good</SelectItem>
                <SelectItem value="Fair">Fair</SelectItem>
                <SelectItem value="Poor">Poor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-gray-700 font-medium text-sm">Key Features (comma-separated)</Label>
            <Input placeholder="Leather seats, Sunroof, Navigation, Backup camera" value={form.features} onChange={e => setForm(p => ({ ...p, features: e.target.value }))} className="h-10 rounded-xl" data-testid="social-features" />
          </div>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={loading}
          className="mt-5 w-full h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg hover:opacity-90"
          data-testid="social-generate-btn"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Listings</>}
        </Button>
      </div>

      {/* Results */}
      {listing && (
        <div className="space-y-6">
          {/* Facebook Marketplace */}
          {listing.facebook && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="social-facebook-result">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500 flex items-center gap-3">
                <Facebook className="w-5 h-5 text-white" />
                <span className="font-bold text-white">Facebook Marketplace</span>
              </div>
              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Listing Title</span>
                    <button onClick={() => copyToClipboard(listing.facebook.title, 'fb-title')} className="text-gray-400 hover:text-blue-600 transition-colors" data-testid="copy-fb-title">
                      {copiedField === 'fb-title' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-lg font-bold text-gray-900 bg-gray-50 p-3 rounded-xl">{listing.facebook.title}</p>
                </div>
                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</span>
                    <button onClick={() => copyToClipboard(listing.facebook.description, 'fb-desc')} className="text-gray-400 hover:text-blue-600 transition-colors" data-testid="copy-fb-desc">
                      {copiedField === 'fb-desc' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-700 whitespace-pre-wrap">{listing.facebook.description}</div>
                </div>
                {/* Tags */}
                {listing.facebook.tags && listing.facebook.tags.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search Tags</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {listing.facebook.tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Instagram */}
          {listing.instagram && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="social-instagram-result">
              <div className="px-6 py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 flex items-center gap-3">
                <Instagram className="w-5 h-5 text-white" />
                <span className="font-bold text-white">Instagram</span>
              </div>
              <div className="p-6 space-y-4">
                {/* Caption */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Post Caption</span>
                    <button onClick={() => copyToClipboard(listing.instagram.caption, 'ig-caption')} className="text-gray-400 hover:text-purple-600 transition-colors" data-testid="copy-ig-caption">
                      {copiedField === 'ig-caption' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-700 whitespace-pre-wrap">{listing.instagram.caption}</div>
                </div>
                {/* Story Text */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Story Text</span>
                    <button onClick={() => copyToClipboard(listing.instagram.story_text, 'ig-story')} className="text-gray-400 hover:text-purple-600 transition-colors" data-testid="copy-ig-story">
                      {copiedField === 'ig-story' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl text-sm font-medium text-gray-800">{listing.instagram.story_text}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
